// Las facturas de la carga con sus adjuntos, dentro del plegable «Facturas»
// de la ficha. El generador (packing list, HS code…) no vive aquí: lo abren el
// chip «Packing list» del recorrido y el botón «Generar documentación».
//
// Facturas: la lista sale de los pedidos de la carga. Vacía significa «aún sin
// facturar», no un fallo, y se explica. Los adjuntos de cada factura se piden al
// desplegarla (no todas de golpe: cada una es una llamada a Power Automate).
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader } from '@/components/common/Loader'
import { useFacturasCarga } from '@/hooks/useCargasSalida'
import { cargasSalidaApi } from '@/services/api'
import type { CargaSalida, FacturaCarga } from '@/types'
import { descargarBase64, getMimeType } from '@/utils/base64'

interface Props {
  c: CargaSalida
  dark: boolean
}

function FacturaItem({ c, f, dark }: { c: CargaSalida; f: FacturaCarga; dark: boolean }) {
  const { t } = useTranslation()
  const [abierta, setAbierta] = useState(false)
  const [errorDescarga, setErrorDescarga] = useState(false)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cargas-salida', c.carga, 'facturas', f.recidFactura, 'adjuntos'],
    queryFn: () => cargasSalidaApi.getFacturaAdjuntos(c.carga, f.recidFactura),
    enabled: abierta,
    staleTime: 5 * 60_000,
    retry: 1,
  })
  const adjuntos = data?.data ?? []

  return (
    <li className={`rounded-lg border ${dark ? 'border-white/8' : 'border-slate-200'}`}>
      <button
        type="button"
        onClick={() => setAbierta(v => !v)}
        aria-expanded={abierta}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left cursor-pointer"
      >
        <span className={`font-mono text-[12px] font-semibold ${dark ? 'text-slate-100' : 'text-slate-800'}`}>{f.factura}</span>
        <span className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
          {t('cargasSalida.ficha.documentos.pedido', { pedido: f.pedido })}
        </span>
      </button>
      {abierta && (
        <div className={`px-3 pb-2 border-t ${dark ? 'border-white/8' : 'border-slate-200'}`}>
          {isLoading && <Loader label={t('cargasSalida.ficha.documentos.cargandoAdjuntos')} />}
          {isError && (
            <p className={`text-[11px] pt-2 ${dark ? 'text-red-400' : 'text-red-600'}`}>
              {t('cargasSalida.ficha.documentos.errorAdjuntos')}
            </p>
          )}
          {!isLoading && !isError && adjuntos.length === 0 && (
            <p className={`text-[11px] pt-2 ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
              {t('cargasSalida.ficha.documentos.sinAdjuntos')}
            </p>
          )}
          <ul className="pt-1">
            {adjuntos.map((a, i) => (
              <li key={a.documentid ?? `${a.orden ?? i}`} className="flex items-center justify-between gap-2 py-1">
                <span className={`text-[11px] truncate ${dark ? 'text-slate-200' : 'text-slate-700'}`} title={a.nombreArchivo || a.nombre}>
                  {a.nombreArchivo || a.nombre}
                </span>
                <button
                  type="button"
                  disabled={!a.contenido}
                  onClick={() => {
                    setErrorDescarga(false)
                    try {
                      descargarBase64(a.contenido as string, a.nombreArchivo || a.nombre, getMimeType(a.tipo))
                    } catch {
                      setErrorDescarga(true)
                    }
                  }}
                  className="shrink-0 text-[11px] font-semibold text-sky-600 hover:text-sky-500 hover:underline cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('cargasSalida.ficha.documentos.descargar')}
                </button>
              </li>
            ))}
          </ul>
          {errorDescarga && (
            <p className={`text-[11px] ${dark ? 'text-red-400' : 'text-red-600'}`}>
              {t('cargasSalida.ficha.documentos.errorDescarga')}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

export function FichaDocumentos({ c, dark }: Props) {
  const { t } = useTranslation()
  const { facturas, isLoading, isError } = useFacturasCarga(c.carga)

  return (
    <div className="flex flex-col gap-2">
      {isLoading && <Loader label={t('cargasSalida.ficha.documentos.cargandoFacturas')} />}
      {isError && (
        <p className={`text-[11px] ${dark ? 'text-red-400' : 'text-red-600'}`}>
          {t('cargasSalida.ficha.documentos.errorFacturas')}
        </p>
      )}
      {!isLoading && !isError && facturas.length === 0 && (
        <div>
          <p className={`text-[12px] font-semibold ${dark ? 'text-slate-200' : 'text-slate-700'}`}>
            {t('cargasSalida.ficha.documentos.sinFacturas')}
          </p>
          <p className={`text-[11px] mt-0.5 ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
            {t('cargasSalida.ficha.documentos.sinFacturasExplica')}
          </p>
        </div>
      )}
      <ul className="flex flex-col gap-2">
        {facturas.map(f => <FacturaItem key={f.recidFactura} c={c} f={f} dark={dark} />)}
      </ul>
    </div>
  )
}
